import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";
import { Kdb } from "./kdb";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = React.memo(TooltipPrimitive.Root);

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipArrow = TooltipPrimitive.Arrow;

export const TOOLTIP_SMALL_DELAY = 500;

const TooltipContent = ({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) => (
  <TooltipPrimitive.Content
    sideOffset={sideOffset}
    className={cn(
      "typo-body-xs-regular-paragraph fade-in-0 zoom-in-95 data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 animate-in overflow-hidden rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-font-secondary shadow-sm data-[state=closed]:animate-out dark:border-slate-800 dark:bg-slate-950 dark:text-slate-50",
      className,
    )}
    {...props}
  />
);

export { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger };

type BasicTooltipProps = {
  children: React.ReactNode;
  content?: React.ReactNode;
  asChild?: boolean;
  tooltipClassName?: string;
  side?: React.ComponentProps<typeof TooltipContent>["side"];
  sideOffset?: React.ComponentProps<typeof TooltipContent>["sideOffset"];
  align?: React.ComponentProps<typeof TooltipContent>["align"];
} & Pick<React.ComponentProps<typeof Tooltip>, "delayDuration">;

export const BasicTooltip: React.FC<BasicTooltipProps> = ({
  children,
  content,
  side = "bottom",
  sideOffset,
  align = "center",
  asChild = false,
  tooltipClassName,
  delayDuration,
}) => (
  <Tooltip delayDuration={delayDuration}>
    <TooltipTrigger asChild={asChild} type={asChild ? undefined : "button"}>
      {children}
    </TooltipTrigger>
    {content
      ? createPortal(
          <TooltipContent side={side} align={align} className={tooltipClassName} sideOffset={sideOffset}>
            {content}
          </TooltipContent>,
          document.body,
        )
      : null}
  </Tooltip>
);

type TextTooltipProps = Omit<BasicTooltipProps, "content"> & {
  text?: string;
};

export const TextTooltip: React.FC<TextTooltipProps> = ({ text, ...props }) => (
  <BasicTooltip {...props} content={text} />
);

type KeyboardShortcut = string | React.ReactElement;

type KeyboardShortcutTooltipProps = Omit<BasicTooltipProps, "content"> & {
  shortcuts?: KeyboardShortcut[]; // any JSX here must have a key
};

export const KeyboardShortcutTooltip: React.FC<KeyboardShortcutTooltipProps> = ({ shortcuts, ...props }) => (
  <BasicTooltip
    {...props}
    content={
      <div className="flex items-center gap-1">
        {shortcuts?.map((shortcut, index) => {
          const isString = typeof shortcut === "string";
          const itemKey = isString ? `str-${shortcut}-${index}` : shortcut.key; // non-null by type

          return (
            <React.Fragment key={itemKey}>
              {isString ? (
                <Kdb name={shortcut} className="animate-[wiggle_1s_ease-in-out_infinite] uppercase" />
              ) : (
                shortcut
              )}
              {index < shortcuts.length - 1 && <span className="mx-0.5">+</span>}
            </React.Fragment>
          );
        })}
      </div>
    }
    delayDuration={1000}
    tooltipClassName="shadow-sm border-slate-100 p-1"
  />
);
